import type { FieldPath, FieldValues } from 'react-hook-form'
import type { Style } from 'inlines'
import { FormField } from '../FormField'
import { InputField } from '../../input/InputField'
import { Checkbox } from '../../checkbox/Checkbox'
import { useFieldRequired } from '../formSchemaContext'
import Column from '@app/components/layout/Column'

export type CheckboxOption<V extends string = string> = {
  value: V
  label: string
}

export type CheckboxGroupFieldProps<
  T extends FieldValues = FieldValues,
  V extends string = string,
> = {
  name: FieldPath<T>
  label?: string
  labelStyle?: Style
  required?: boolean
  options: CheckboxOption<V>[]
}

export const CheckboxGroupField = <
  T extends FieldValues = FieldValues,
  V extends string = string,
>({
  name,
  label,
  labelStyle,
  required,
  options,
}: CheckboxGroupFieldProps<T, V>) => {
  const fieldRequired = useFieldRequired(name)
  const isRequired = required ?? fieldRequired

  return (
    <FormField<T>
      name={name}
      render={({ value, onChange, errorMessage }) => {
        const selected = (value as V[]) ?? []
        const toggle = (opt: V) => {
          const next = selected.includes(opt)
            ? selected.filter((v) => v !== opt)
            : [...selected, opt]
          onChange(next)
        }
        return (
          <InputField
            label={label}
            labelStyle={labelStyle}
            required={isRequired}
            errorMessage={errorMessage}
          >
            <Column gap="0.5rem" alignItems="stretch">
              {options.map((opt) => (
                <Checkbox
                  key={opt.value}
                  label={opt.label}
                  selected={selected.includes(opt.value)}
                  onClick={() => toggle(opt.value)}
                  labelSize="small"
                />
              ))}
            </Column>
          </InputField>
        )
      }}
    />
  )
}
